/**
 * Toolbar com search + chips de Env e Status.
 *
 * - Search é debounced 300ms (via useEffect).
 * - Chips multi-select (toggle on click).
 * - State controlado pelo parent via `value` + `onChange`.
 */
import { Card, Chip, Group, Switch, TextInput } from '@mantine/core'
import { IconSearch } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import type { FilterState, HealthBucket } from '../types'

interface Props {
  value: FilterState
  onChange: (next: FilterState) => void
}

const ENV_OPTIONS = ['DEV', 'QA', 'HML', 'PRD'] as const
const STATUS_OPTIONS: { value: HealthBucket; label: string }[] = [
  { value: 'healthy', label: 'Healthy' },
  { value: 'degraded', label: 'Degraded' },
  { value: 'failed', label: 'Failed' },
  { value: 'idle', label: 'Idle' },
]

export function FiltersToolbar({ value, onChange }: Props) {
  const [search, setSearch] = useState(value.search)

  // Debounce: propaga mudança após 300ms sem digitar.
  useEffect(() => {
    if (search === value.search) return
    const t = window.setTimeout(() => {
      onChange({ ...value, search })
    }, 300)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const toggleEnv = (env: string) => {
    const next = value.envs.includes(env)
      ? value.envs.filter((e) => e !== env)
      : [...value.envs, env]
    onChange({ ...value, envs: next })
  }
  const toggleStatus = (s: HealthBucket) => {
    const next = value.statuses.includes(s)
      ? value.statuses.filter((x) => x !== s)
      : [...value.statuses, s]
    onChange({ ...value, statuses: next })
  }

  return (
    <Card>
      <Group gap="md" align="center" wrap="wrap">
        <TextInput
          aria-label="buscar apps"
          placeholder="Buscar por slug, hostname, tenant..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ flex: '1 1 280px', minWidth: 240 }}
        />
        <Group gap={6} align="center">
          <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--ai-text-muted)', fontWeight: 600 }}>
            Env
          </span>
          {ENV_OPTIONS.map((env) => (
            <Chip
              key={env}
              size="xs"
              variant="light"
              color="inovareTeal"
              checked={value.envs.includes(env)}
              onChange={() => toggleEnv(env)}
            >
              {env}
            </Chip>
          ))}
        </Group>
        <Group gap={6} align="center">
          <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--ai-text-muted)', fontWeight: 600 }}>
            Status
          </span>
          {STATUS_OPTIONS.map((s) => (
            <Chip
              key={s.value}
              size="xs"
              variant="light"
              color="inovareTeal"
              checked={value.statuses.includes(s.value)}
              onChange={() => toggleStatus(s.value)}
            >
              {s.label}
            </Chip>
          ))}
        </Group>
        <Switch
          size="xs"
          label="Mostrar suspensas"
          checked={!!value.includeDisabled}
          onChange={(e) =>
            onChange({ ...value, includeDisabled: e.currentTarget.checked })
          }
        />
      </Group>
    </Card>
  )
}

export default FiltersToolbar
